import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// v3 introduces GA4. Earlier copy explicitly said there was no analytics provider.
// Obtain a fresh choice before activating either optional Google destination.
export const CONSENT_STORAGE_KEY = 'bukietowa.cookie-consent.v3';

export type ConsentCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

export interface ConsentPreferences {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface OptionalIntegration {
  id: string;
  name: string;
  category: Exclude<ConsentCategory, 'necessary'>;
  description: string;
  sharedWith: string[];
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

const integrationRegistry = new Map<string, OptionalIntegration>();

export function registerOptionalIntegration(integration: OptionalIntegration) {
  integrationRegistry.set(integration.id, integration);
}

export function getOptionalIntegrations() {
  return [...integrationRegistry.values()];
}

export function getOptionalIntegration(id: string) {
  return integrationRegistry.get(id);
}

registerOptionalIntegration({
  id: 'google-maps-embed',
  name: 'Mapy Google',
  category: 'functional',
  description: 'Interaktywne mapy lokalizacji naszych kwiaciarni.',
  sharedWith: ['Google Maps'],
});

registerOptionalIntegration({
  id: 'google-ads-purchase',
  name: 'Google Ads',
  category: 'marketing',
  description: 'Pomiar skuteczności reklam na podstawie opłaconych zamówień.',
  sharedWith: ['Google Ads'],
});

registerOptionalIntegration({
  id: 'google-analytics', name: 'Google Analytics 4', category: 'analytics',
  description: 'Pomiar odwiedzin, oglądanych produktów, koszyka i opłaconych zamówień.',
  sharedWith: ['Google Analytics'],
});

function isStoredPreferences(value: unknown): value is ConsentPreferences {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ConsentPreferences>;
  return candidate.necessary === true
    && typeof candidate.functional === 'boolean'
    && typeof candidate.analytics === 'boolean'
    && typeof candidate.marketing === 'boolean';
}

function readPreferences(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isStoredPreferences(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

interface ConsentContextValue {
  isReady: boolean;
  hasSelection: boolean;
  preferences: ConsentPreferences | null;
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
  hasConsent: (category: ConsentCategory) => boolean;
  canUseIntegration: (id: string) => boolean;
  acceptAll: () => void;
  savePreferences: (preferences: ConsentPreferences) => void;
  grantConsent: (category: Exclude<ConsentCategory, 'necessary'>) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setPreferences(readPreferences());
    setIsReady(true);
    const synchronize = (event: StorageEvent) => {
      if (event.key === CONSENT_STORAGE_KEY || event.key === null) setPreferences(readPreferences());
    };
    window.addEventListener('storage', synchronize);
    return () => window.removeEventListener('storage', synchronize);
  }, []);

  const persist = useCallback((next: ConsentPreferences) => {
    setPreferences(next);
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Consent state still applies for this visit when browser storage is unavailable.
    }
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, functional: true, analytics: true, marketing: true });
    setSettingsOpen(false);
  }, [persist]);

  const savePreferences = useCallback((next: ConsentPreferences) => {
    persist({ ...next, necessary: true });
    setSettingsOpen(false);
  }, [persist]);

  const grantConsent = useCallback((category: Exclude<ConsentCategory, 'necessary'>) => {
    persist({ ...(preferences ?? DEFAULT_PREFERENCES), necessary: true, [category]: true });
  }, [persist, preferences]);

  const value = useMemo<ConsentContextValue>(() => ({
    isReady,
    hasSelection: preferences !== null,
    preferences,
    settingsOpen,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
    hasConsent: (category) => category === 'necessary' || Boolean(preferences?.[category]),
    canUseIntegration: (id) => {
      const integration = getOptionalIntegration(id);
      return integration ? Boolean(preferences?.[integration.category]) : false;
    },
    acceptAll,
    savePreferences,
    grantConsent,
  }), [
    acceptAll,
    grantConsent,
    isReady,
    preferences,
    savePreferences,
    settingsOpen,
  ]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) throw new Error('useConsent must be used within ConsentProvider');
  return context;
}
